/**
 * Ownership tracking for the browser-persisted state that belongs to one signed-in user.
 *
 * Logout only clears the auth tokens, so cached entries such as the profile blob read by
 * the topbar, the layout and ~15 feature screens used to outlive the session. The next
 * user signing in on the same browser was then shown the previous user's name, avatar,
 * role and onboarding flags, because every reader treats a present cache as its own.
 *
 * Each entry is stamped with the user it was written for and dropped as soon as a
 * different user is authenticated, which forces a fresh fetch on the first render after
 * login instead of after a manual reload.
 *
 * Only persisted state is tracked here. In-memory caches (`AppConsts` statics, root
 * services) cannot leak between users because every login, logout, impersonation and
 * linked-account switch navigates with `location.href`, which re-evaluates the bundle.
 */
export class UserScopedState {
    private static readonly ownerKey = 'ITP.UserState.Owner';

    /**
     * Cached data and UI state derived from the signed-in user. Dropped both on logout and
     * whenever a different user is authenticated.
     */
    private static readonly userScopedKeys: string[] = [
        // Profile caches read by the topbar, layout, my-profile and feature screens
        'MyProfileData',
        'MyProfileDetails',
        'MyProfileDataSaved',
        'imagePath',
        // First-login / quick-start onboarding progress
        'checkLoginSuccess',
        'checkLoginAttemtsResult',
        'FirstTimeCheck',
        'isDownloadModalVisible',
        'tsreminder_skip_state',
        'CheckCompanyProfile',
        'CheckCompanySaveData',
        'CheckOrganizationUnit',
        'CheckInviteUserUnit',
        'checkQuickStartRoute',
        'CompanyProfilePermission',
        // Per-user grid/tab/date selections
        'storageLeaveApprovals',
        'storageLeaveApprovalsKey',
        'storageNewSubmittedTimesheet',
        'storageNewSubmittedTimesheetKey',
        'storageNewTimesheetApprovals',
        'storageNewTimesheetApprovalsKey',
        'appspenttime',
        'dashboardProductivity',
        'isViewAllFromDashboard',
        'selectedDate',
        'selectedCalendarDate',
        'SelectedDateFromTimeAndAttendance',
        'SeeAll',
        'UserID_screenShot',
        'UserTenant_screenShot',
        'BulkUpdateManualTimePending',
        'testArc',
        'testProj',
    ];

    /**
     * Written by the login page after authentication succeeds but before the redirect, so
     * they belong to the session that is about to start and must survive the first
     * post-login bootstrap. Cleared on logout only.
     */
    private static readonly loginHandshakeKeys: string[] = [
        'firstTimeLoggedIn',
        'tsreminder_session_id',
    ];

    /** Tab session copies of dashboard payloads. These survive a reload in the same tab. */
    private static readonly sessionKeys: string[] = [
        'applicationWiseSpentTime',
        // Release-note popup auto-opens at most once per login
        'releaseNoteAutoShown',
    ];

    /**
     * Drops everything cached for the user that just signed out, including the keys the
     * login page stamps for the next session.
     */
    static clearOnLogout(): void {
        this.remove(this.userScopedKeys);
        this.remove(this.loginHandshakeKeys);
        this.remove([this.ownerKey]);
        this.clearSessionKeys();
    }

    /**
     * Drops the previous session's dashboard and profile cache before a new login
     * finishes navigating in. Same-user re-login (password, Microsoft, or desktop)
     * must not reuse the last session's widgets.
     * Login handshake keys are left intact; the login page writes those for the session
     * that is about to start.
     */
    static beginFreshSession(): void {
        this.remove(this.userScopedKeys);
        this.remove([this.ownerKey]);
        this.clearSessionKeys();
    }

    /**
     * Discards state left behind by a different user and records `userId` as the owner of
     * everything cached from here on. Pass a null `userId` for an anonymous page, which
     * drops the cache without claiming it.
     */
    static claimFor(userId?: number, tenantId?: number): void {
        const owner = userId == null ? null : `${tenantId == null ? 'host' : tenantId}/${userId}`;

        if (owner !== null && this.read(this.ownerKey) === owner) {
            return;
        }

        this.remove(this.userScopedKeys);
        this.clearSessionKeys();

        if (owner === null) {
            this.remove([this.ownerKey]);
        } else {
            this.write(this.ownerKey, owner);
        }
    }

    private static read(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch {
            // Private mode / disabled storage: treat as unowned so callers re-fetch.
            return null;
        }
    }

    private static write(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch {
            // Quota or private-mode failure. Without a stamp the cache is dropped on the
            // next bootstrap, which is the safe direction.
        }
    }

    private static remove(keys: string[]): void {
        keys.forEach((key) => {
            try {
                localStorage.removeItem(key);
            } catch {
                // Ignore storage failures.
            }
        });
    }

    private static clearSessionKeys(): void {
        this.sessionKeys.forEach((key) => {
            try {
                sessionStorage.removeItem(key);
            } catch {
                // Ignore storage failures.
            }
        });
    }
}
